// 新デプロイでチャンク名が変わり、既に開いていたタブが古いチャンクを
// 要求して 404 → ChunkLoadError になる問題への対処。
// 検知したら 1 回だけ自動リロードして新しい HTML + チャンク名を取得し直す。
// （リロードループを防ぐため、直近リロードからの経過時間でガードする）

const RELOAD_GUARD_KEY = "chunk-reload-at"
// このミリ秒以内に既にチャンクリロードしていたら、再リロードしない
// （リロード後もまだ古いチャンクを要求する＝別要因の可能性があるため諦める）
const RELOAD_GUARD_WINDOW_MS = 10_000

// ChunkLoadError かどうかを判定する。
// - webpack が投げる Error は name === "ChunkLoadError"
// - メッセージに "Loading chunk" / "Failed to load chunk" を含む
// - 動的 import の失敗で "Failed to fetch dynamically imported module" になることも
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false

  const err = error as { name?: unknown; message?: unknown }
  const name = typeof err.name === "string" ? err.name : ""
  const message = typeof err.message === "string" ? err.message : ""

  if (name === "ChunkLoadError") return true

  return (
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Failed to load chunk/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  )
}

// ChunkLoadError を検知したときに 1 回だけリロードする。
// 直近 RELOAD_GUARD_WINDOW_MS 以内に既にリロード済みなら false を返して何もしない。
export function reloadForChunkError(): boolean {
  if (typeof window === "undefined") return false

  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY) || "0")
    // Date.now() は環境によってはテストで固定されるが、本番では通常どおり動く
    const now = Date.now()
    if (last && now - last < RELOAD_GUARD_WINDOW_MS) {
      // 直近でリロード済み → ループ防止のため諦める
      return false
    }
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(now))
  } catch {
    // sessionStorage が使えない環境でもリロード自体は試みる
  }

  window.location.reload()
  return true
}

// window.onerror / unhandledrejection から ChunkLoadError を拾って自動リロードする。
// アプリのルートで一度だけ呼ぶ。二重登録を防ぐためフラグで管理する。
let listenersInstalled = false

export function installChunkErrorAutoReload(): () => void {
  if (typeof window === "undefined" || listenersInstalled) {
    return () => {}
  }
  listenersInstalled = true

  const handleError = (event: ErrorEvent) => {
    if (isChunkLoadError(event.error) || isChunkLoadError({ message: event.message })) {
      reloadForChunkError()
    }
  }

  const handleRejection = (event: PromiseRejectionEvent) => {
    if (isChunkLoadError(event.reason)) {
      reloadForChunkError()
    }
  }

  window.addEventListener("error", handleError)
  window.addEventListener("unhandledrejection", handleRejection)

  return () => {
    window.removeEventListener("error", handleError)
    window.removeEventListener("unhandledrejection", handleRejection)
    listenersInstalled = false
  }
}
